// =============================================
// COMPONENTE LAYOUT - LAYOUT PRINCIPAL
// =============================================
// Arquivo: Layout.tsx
// Descrição: Componente principal que gerencia o layout da aplicação
// =============================================

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import NotificationCenter from '../FlowEditor/NotificationCenter';
import './Layout.css';

// =============================================
// INTERFACES
// =============================================

interface LayoutProps {
  children: React.ReactNode;
}

// =============================================
// MAPEAMENTO DE ROTAS PARA TÍTULOS
// =============================================

const routeTitles: { [key: string]: string } = {
  '/dashboard': 'Dashboard',
  '/veiculos': 'Cadastro de Veículos',
  '/motoristas': 'Cadastro de Motoristas',
  '/equipamentos': 'Cadastro de Equipamentos',
  '/itens-checklist': 'Itens de Checklist',
  '/grupos-checklist': 'Grupos de Checklist',
  '/ocorrencias': 'Ocorrências',
  '/checklists': 'Lista de Checklists',
  '/agendamentos': 'Agendamentos',
  '/abastecimentos': 'Abastecimentos',
  '/produtividade': 'Produtividade',
  '/manutencoes-preventivas': 'Manutenções Preventivas',
  '/manutencoes-corretivas': 'Manutenções Corretivas',
  '/pneus': 'Pneus',
  '/compliance': 'Compliance',
  '/tco': 'TCO - Custo Total',
  '/notifications': 'Notificações',
  '/configuracoes': 'Configurações',
  '/montar-carga': 'Montagem de Paletes',
  '/montar-carga/novo': 'Nova Operação de Montagem',
  '/montar-carga/filiais': 'Filiais/Unidades',
  '/montar-carga/areas': 'Áreas de Entrega',
  '/montar-carga/agendamentos': 'Agendamentos de Montagem',
  '/conferencia': 'Conferências de Carga',
  '/conferencia/novo': 'Nova Conferência',
  '/conferencia/pre-carregamento': 'Pré-Carregamento',
  '/conferencia/carregamento': 'Carregamento',
  '/conferencia/descarregamento': 'Descarregamento',
  '/conferencia/produtividade': 'Produtividade de Conferências',
  '/conferentes': 'Conferentes',
  '/clientes': 'Clientes',
  '/pedidos': 'Pedidos Importados'
};

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

/**
 * Componente Layout - Layout principal da aplicação
 * 
 * @param children - Conteúdo da página
 * @returns JSX.Element - Layout completo da aplicação
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();

  // =============================================
  // EFEITOS E MANIPULAÇÃO DE ESTADO
  // =============================================

  /**
   * Fecha o sidebar mobile quando a rota muda
   */
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  /**
   * Ajusta o layout baseado no tamanho da tela
   */
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // =============================================
  // MANIPULAÇÃO DE EVENTOS
  // =============================================

  /**
   * Alterna colapso do sidebar
   */
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  /**
   * Alterna sidebar mobile
   */
  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  /**
   * Fecha sidebar mobile quando clica fora (apenas mobile)
   */
  const handleContentClick = () => {
    if (window.innerWidth <= 768 && mobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  };

  /**
   * Obtém título da página baseado na rota
   * @returns string - Título da página
   */
  const getPageTitle = (): string => {
    return routeTitles[location.pathname] || 'Dashboard';
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  const sidebarWidth = isMobile ? 260 : (sidebarCollapsed ? 70 : 260);
  const mainContentMargin = isMobile ? 0 : sidebarWidth;

  return (
    <div className="layout">
      {/* Sidebar */}
      <div
        className={`sidebar-container ${mobileSidebarOpen ? 'mobile-open' : ''}`}
        style={{ width: sidebarWidth }}
      >
        <Sidebar
          collapsed={sidebarCollapsed && !isMobile}
          onToggle={isMobile ? toggleMobileSidebar : toggleSidebar}
          isMobile={isMobile}
        />
      </div>

      {/* Overlay para mobile */}
      {isMobile && mobileSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Conteúdo Principal */}
      <div 
        className="main-content"
        style={{ marginLeft: mainContentMargin }}
        onClick={handleContentClick}
      >
        {/* Header */}
        <Header
          title={getPageTitle()}
          onMenuToggle={toggleMobileSidebar}
        />

        {/* Área de Conteúdo */}
        <main className="content-area">
          {children}
        </main>

        {/* Footer (opcional) */}
        <footer className="app-footer">
          <div className="footer-content">
            <p>&copy; {new Date().getFullYear()} ROBOT.IA - Sistema de Checklist. Todos os direitos reservados.</p>
            <div className="footer-links">
              <span>v1.0.0</span>
              <span>•</span>
              <a href="#privacy">Privacidade</a>
              <span>•</span>
              <a href="#terms">Termos</a>
              <span>•</span>
              <a href="#support">Suporte</a>
            </div>
          </div>
        </footer>
      </div>

      {/* Notification Center - Toast de notificações em tempo real */}
      <NotificationCenter
        position="top-right"
        maxToasts={3}
        toastDuration={5000}
        enableSound={true}
        enableDesktop={true}
      />
    </div>
  );
};

export default Layout;
